/** biome-ignore-all lint/suspicious/noArrayIndexKey: Area positions define their stable layout order. */
import { cn } from "tailwind-variants";
import type {
	ApiBlockGridAreaModel,
	ApiBlockGridItemModel,
} from "#/_generated/umbraco/delivery-api/models";
import {
	Grid,
	GridColumn,
} from "#/_packages/umbrella/integrations/umbraco/components/ui/grid";
import { BlockGridItemRenderer } from "#/_packages/umbrella/integrations/umbraco/utils/blockgrid-item-renderer.tsx";
import { BlockMediaScope } from "../utils/media-composition";

interface IBlockGridAreaProps {
	id?: string | undefined;
	areas?: ApiBlockGridAreaModel[] | undefined;
	className?: string | undefined;
}

export function BlockGridArea({ areas, className, id }: IBlockGridAreaProps) {
	if (!areas) return;

	return areas && areas.length > 0 ? (
		<Grid id={id ?? ""} className={cn(className)} columns={12}>
			{areas?.map((area, key) => {
				return (
					<GridColumn
						columnSpan={area.columnSpan}
						className={"_layout _block-grid-item [4/6]"}
						key={`block-grid-area-${key}`}
					>
						<Grid
							className="_area _block-grid [5/6]"
							columns={area.columnSpan}
							// @ts-expect-error
							style={{ "--umb-columns": area.columnSpan }}
						>
							{area.items?.map((areaItem) => {
								if (!shouldRenderAreaItem(areaItem)) return null;

								return (
									<GridColumn
										className={cn(
											`grid-column-item-${areaItem.content.contentType}`,
											getAreaItemClassName(areaItem),
											"[6/6] _area",
										)}
										columnSpan={areaItem.columnSpan}
										key={areaItem.content.id}
									>
										<div
											className={`_block-grid-item _block-grid-item--${areaItem.content.contentType.toLowerCase()}`}
										>
											<BlockGridItem item={areaItem} />
										</div>
									</GridColumn>
								);
							})}
						</Grid>
					</GridColumn>
				);
			})}
		</Grid>
	) : null;
}

interface IBlockGridItem {
	item: ApiBlockGridItemModel;
}

function BlockGridItem({ item }: IBlockGridItem) {
	return (
		<BlockMediaScope columnSpan={item.columnSpan}>
			<BlockGridItemRenderer model={item} />
		</BlockMediaScope>
	);
}

type AreaItemSettings = {
	hideBlock?: boolean | null;
	publishStartDate?: string | null;
	publishEndDate?: string | null;
	responsiveMobilePortrait?: number | string | null;
	responsiveMobileLandscape?: number | string | null;
	responsiveTabletPortrait?: number | string | null;
	alignmentHorizontal?: string | null;
	alignmentSelfVertical?: string | null;
};

function getAreaItemSettings(item: ApiBlockGridItemModel) {
	return item.settings?.properties as AreaItemSettings | undefined;
}

function parseDate(value?: string | null) {
	if (!value) return;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? undefined : date;
}

function shouldRenderAreaItem(item: ApiBlockGridItemModel) {
	const settings = getAreaItemSettings(item);
	if (!settings) return true;
	if (settings.hideBlock) return false;

	const now = new Date();
	const start = parseDate(settings.publishStartDate);
	if (start && start > now) return false;
	const end = parseDate(settings.publishEndDate);
	if (end && end < now) return false;

	return true;
}

// Static class maps so Tailwind can detect the classes at build time.
const spanXs: Record<number, string> = {
	1: "col-span-1",
	2: "col-span-2",
	3: "col-span-3",
	4: "col-span-4",
	5: "col-span-5",
	6: "col-span-6",
	7: "col-span-7",
	8: "col-span-8",
	9: "col-span-9",
	10: "col-span-10",
	11: "col-span-11",
	12: "col-span-12",
};
const spanSm: Record<number, string> = {
	1: "sm:col-span-1",
	2: "sm:col-span-2",
	3: "sm:col-span-3",
	4: "sm:col-span-4",
	5: "sm:col-span-5",
	6: "sm:col-span-6",
	7: "sm:col-span-7",
	8: "sm:col-span-8",
	9: "sm:col-span-9",
	10: "sm:col-span-10",
	11: "sm:col-span-11",
	12: "sm:col-span-12",
};
const spanMd: Record<number, string> = {
	1: "md:col-span-1",
	2: "md:col-span-2",
	3: "md:col-span-3",
	4: "md:col-span-4",
	5: "md:col-span-5",
	6: "md:col-span-6",
	7: "md:col-span-7",
	8: "md:col-span-8",
	9: "md:col-span-9",
	10: "md:col-span-10",
	11: "md:col-span-11",
	12: "md:col-span-12",
};
const startLg: Record<number, string> = {
	1: "lg:col-start-1",
	2: "lg:col-start-2",
	3: "lg:col-start-3",
	4: "lg:col-start-4",
	5: "lg:col-start-5",
	6: "lg:col-start-6",
	7: "lg:col-start-7",
	8: "lg:col-start-8",
	9: "lg:col-start-9",
	10: "lg:col-start-10",
	11: "lg:col-start-11",
	12: "lg:col-start-12",
};

function toCols(value?: number | string | null, fallback = 12) {
	const n = Math.trunc(Number(value));
	return Number.isFinite(n) && n >= 1 && n <= 12 ? n : fallback;
}

function getDesktopStartClassName(alignment?: string | null, span = 12) {
	const horizontal = alignment?.toLowerCase();
	if (horizontal !== "center" && horizontal !== "end") return;

	const diff = 12 - Math.trunc(span);
	if (diff < 0) return;

	const start = horizontal === "center" ? Math.trunc(diff / 2) + 1 : diff + 1;
	return startLg[start];
}

function getAlignmentVerticalClassName(alignment?: string | null) {
	switch (alignment?.toLowerCase()) {
		case "stretch":
			return "g-align-v-stretch";
		case "start":
			return "g-align-v-start";
		case "center":
			return "g-align-v-center";
		case "end":
			return "g-align-v-end";
		default:
			return;
	}
}

function getAreaItemClassName(item: ApiBlockGridItemModel) {
	const settings = getAreaItemSettings(item);

	const mobilePortrait = toCols(settings?.responsiveMobilePortrait);
	const mobileLandscape = toCols(settings?.responsiveMobileLandscape);
	const tabletPortrait = toCols(settings?.responsiveTabletPortrait);

	return cn(
		spanXs[mobilePortrait],
		mobileLandscape !== mobilePortrait && spanSm[mobileLandscape],
		tabletPortrait !== mobileLandscape && spanMd[tabletPortrait],
		getDesktopStartClassName(settings?.alignmentHorizontal, item.columnSpan),
		getAlignmentVerticalClassName(settings?.alignmentSelfVertical),
	);
}
